import { useState, useMemo } from "react";
import { RenderIcon } from "./IconPickerPopover";
import {
  ComparisonRow,
  ModulePricing,
  TierPricing,
  RealEstateKey,
  RealEstatePricingModelMap,
  CategoryMultipliers,
  REAL_ESTATE_LABELS,
  effectiveCategoryPrice,
} from "../types";
import { PricingCalculator } from "./PricingCalculator";
import { FunctionalityMatrix } from "./FunctionalityMatrix";

interface CommercialPreviewProps {
  tierPricing: TierPricing;
  modulePricing: ModulePricing;
  rePricingModel: RealEstatePricingModelMap;
  categoryMultipliers: CategoryMultipliers;
  customPricingThresholdUnits: number;
  customPricingThresholdM2: number;
  comparisonRows: ComparisonRow[];
  onModulePricingChange?: (mp: ModulePricing) => void;
}

const CheckIcon = () => (
  <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-px">
    <svg viewBox="0 0 10 8" className="w-[9px] h-[9px]">
      <polyline points="1 4 3.5 7 9 1" fill="none" stroke="#059669" strokeWidth="2.5" />
    </svg>
  </div>
);


const reKeys = Object.keys(REAL_ESTATE_LABELS) as RealEstateKey[];

type ViewMode = RealEstateKey | "mixed";

const VIEW_OPTIONS: { value: ViewMode; label: string; icon: string }[] = [
  ...reKeys.map((k) => ({
    value: k as ViewMode,
    label: REAL_ESTATE_LABELS[k].label,
    icon: REAL_ESTATE_LABELS[k].icon,
  })),
  { value: "mixed", label: "Mixed-use", icon: "\uD83C\uDFD7\uFE0F" },
];


export const CommercialPreview = ({
  tierPricing,
  modulePricing,
  rePricingModel,
  categoryMultipliers,
  customPricingThresholdUnits,
  customPricingThresholdM2,
  comparisonRows,
  onModulePricingChange: _onModChange,
}: CommercialPreviewProps) => {
  void _onModChange;
  const [viewMode, setViewMode] = useState<ViewMode>("appartement");
  const [showCustomBuilder, setShowCustomBuilder] = useState(false);
  const [customModules, setCustomModules] = useState<Set<string>>(new Set());

  const tierKeys = Object.keys(tierPricing);
  const isMixed = viewMode === "mixed";
  const selectedReKey = isMixed ? null : (viewMode as RealEstateKey);
  const isM2View = selectedReKey ? rePricingModel[selectedReKey] === "m2" : false;

  const allEnabledModules = Object.keys(modulePricing).filter(
    (k) => modulePricing[k].enabled
  );

  const toggleCustomModule = (modKey: string) => {
    setCustomModules((prev) => {
      const next = new Set(prev);
      if (next.has(modKey)) next.delete(modKey); else next.add(modKey);
      return next;
    });
  };

  // Calculate custom plan price
  const customPlanPrice = useMemo(() => {
    if (customModules.size === 0) return { perUnit: 0, perM2: 0 };
    let perUnit = 0;
    let perM2 = 0;
    for (const modKey of customModules) {
      const mod = modulePricing[modKey];
      if (!mod) continue;
      perUnit += mod.pricePerUnit;
      perM2 += mod.pricePerM2;
    }
    return { perUnit, perM2 };
  }, [customModules, modulePricing]);

  return (
    <div className="bg-[#F8F9FC] rounded-lg overflow-hidden border">
      {/* Hero */}
      <div className="text-center py-10 px-6">
        <span className="inline-block bg-blue-50 text-blue-600 text-[11px] font-semibold tracking-wider uppercase px-3 py-1 rounded-full mb-3">
          Transparante prijzen
        </span>
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight leading-tight" style={{ fontFamily: "'Sora', sans-serif" }}>
          Eenvoudig starten,<br />
          <span className="text-blue-600">mee groeien</span> met uw portfolio
        </h2>
        <p className="text-sm text-gray-500 mt-3 max-w-md mx-auto">
          Kies een kant-en-klaar plan of stel uw eigen pakket samen.
        </p>
      </div>

      {/* RE category switcher */}
      <div className="px-6 pb-4">
        <p className="text-[10px] text-gray-500 text-center mb-2">Bekijk prijzen voor:</p>
        <div className="flex flex-wrap justify-center gap-1.5">
          {VIEW_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setViewMode(opt.value)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-medium border transition-colors ${
                viewMode === opt.value
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-600 border-gray-200 hover:border-blue-300"
              }`}
            >
              {opt.icon} {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tier Cards + Custom Builder */}
      <div className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-${tierKeys.length + 1} gap-3 px-6 pb-8`}>
        {/* Pre-configured tier cards */}
        {tierKeys.map((tier) => {
          const config = tierPricing[tier];
          if (!config) return null;
          const isPopular = !!config.isPopular;
          const isFree = !!config.isFree;

          const catPrice = selectedReKey
            ? effectiveCategoryPrice(selectedReKey, config, modulePricing, rePricingModel, categoryMultipliers)
            : 0;

          const includedFeatures = comparisonRows.filter((row) => row.tiers[tier]);
          // All included modules for this tier
          const allModKeys = (config.includedModules || []).filter(
            (k) => modulePricing[k]?.enabled
          );

          return (
            <div
              key={tier}
              className={`bg-white rounded-xl p-5 flex flex-col relative transition-shadow hover:shadow-md ${
                isPopular ? "border-2 border-blue-600 ring-4 ring-blue-600/8" : "border border-gray-200"
              }`}
            >
              {isPopular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[10px] font-semibold tracking-wide px-3 py-1 rounded-full whitespace-nowrap">
                  Meest populair
                </span>
              )}

              <div className="mb-3"><RenderIcon value={config.icon} size={28} /></div>
              <h3 className="font-bold text-base mb-1">{config.label}</h3>

              {isFree ? (
                <div className="mb-1">
                  <span className="text-2xl font-bold text-blue-600" style={{ fontFamily: "'DM Mono', monospace" }}>0&euro;</span>
                </div>
              ) : (
                <div className="mb-1">
                  {!isMixed && selectedReKey && catPrice > 0 ? (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold text-blue-600" style={{ fontFamily: "'DM Mono', monospace" }}>
                          &euro;{isM2View ? catPrice.toFixed(3) : catPrice.toFixed(2)}
                        </span>
                        <span className="text-[11px] text-blue-600 font-medium">
                          {isM2View ? "/m\u00B2" : "/unit"} /mnd
                        </span>
                      </div>
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        met een minimum van &euro;{config.basePrice} /maand
                      </p>
                    </>
                  ) : isMixed ? (
                    <>
                      <p className="text-lg font-bold text-blue-600 mb-0.5">Aangepaste prijszetting</p>
                      <p className="text-[10px] text-gray-500">
                        met een minimum van &euro;{config.basePrice} /maand
                      </p>
                      <a href="#pricing-calculator" className="text-[10px] text-blue-600 font-medium underline hover:no-underline mt-1 inline-block">
                        Bereken uw prijs &darr;
                      </a>
                    </>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className="text-[10px] text-gray-400 italic">vanaf</span>
                      <span className="text-2xl font-bold text-blue-600" style={{ fontFamily: "'DM Mono', monospace" }}>
                        {config.basePrice}&euro;
                      </span>
                      <span className="text-[11px] text-gray-500">/maand</span>
                    </div>
                  )}
                </div>
              )}

              <p className="text-[10px] text-gray-500 mb-3">
                {isFree
                  ? `Gratis tot ${config.freeMaxUnits ?? 3} units \u00B7 ${(config.freeMaxM2 ?? 500).toLocaleString("nl-BE")} m\u00B2 \u00B7 ${config.includedUsers} gebruiker(s)`
                  : `Excl. btw \u00B7 ${config.includedUsers} gebruikers incl.${config.pricePerExtraUser > 0 ? ` \u00B7 +\u20AC${config.pricePerExtraUser}/extra` : ""}`}
              </p>

              <p className="text-xs text-gray-500 leading-relaxed mb-4 min-h-[36px]">{config.description}</p>

              <a
                href="#pricing-calculator"
                onClick={() => {
                  // Pre-select this tier's modules in the custom builder
                  setCustomModules(new Set(config.includedModules || []));
                  setShowCustomBuilder(true);
                }}
                className={`w-full py-2 rounded-md text-xs font-semibold mb-4 transition-colors text-center block ${
                  isPopular ? "bg-blue-600 text-white hover:bg-blue-700" : "border border-gray-200 text-gray-800 hover:border-blue-600 hover:text-blue-600 hover:bg-blue-50"
                }`}
              >
                {isFree ? "Gratis starten" : `Start ${config.label}`}
              </a>

              <div className="h-px bg-gray-200 mb-3" />

              <div className="space-y-1.5 flex-1">
                {/* Included comparison features */}
                {includedFeatures.map((row) => (
                  <div key={row.id} className="flex items-start gap-2 text-[11px] leading-snug">
                    <CheckIcon /><span>{row.label}</span>
                  </div>
                ))}
                {/* All included modules with descriptions */}
                {allModKeys.map((modKey) => {
                  const mod = modulePricing[modKey];
                  if (!mod) return null;
                  return (
                    <div key={modKey} className="flex items-start gap-2 text-[11px] leading-snug">
                      <CheckIcon />
                      <div>
                        <span className="font-medium">{mod.label}</span>
                        {mod.description && <p className="text-[9px] text-gray-400 leading-tight mt-0.5">{mod.description}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Custom plan builder card */}
        <div className="bg-white rounded-xl p-5 flex flex-col border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors">
          <span className="text-2xl mb-3">{"\uD83E\uDDE9"}</span>
          <h3 className="font-bold text-base mb-1">Op maat</h3>

          {isMixed ? (
            /* Mixed-use: always show CTA that scrolls to calculator */
            <>
              <p className="text-xs text-gray-500 leading-relaxed mb-4 flex-1">
                Stel uw eigen plan samen. Vul uw portefeuille in en kies de modules die u nodig heeft.
              </p>
              <a
                href="#pricing-calculator"
                onClick={() => setShowCustomBuilder(true)}
                className="w-full py-2 rounded-md text-xs font-semibold border-2 border-blue-600 text-blue-600 hover:bg-blue-50 transition-colors text-center block"
              >
                Stel je eigen plan samen &darr;
              </a>
            </>
          ) : !showCustomBuilder ? (
            /* Single RE: show CTA then expand inline */
            <>
              <p className="text-xs text-gray-500 leading-relaxed mb-4 flex-1">
                Stel uw eigen plan samen. Kies enkel de modules die u nodig heeft en betaal enkel voor wat u gebruikt.
              </p>
              <button
                onClick={() => setShowCustomBuilder(true)}
                className="w-full py-2 rounded-md text-xs font-semibold border-2 border-blue-600 text-blue-600 hover:bg-blue-50 transition-colors"
              >
                Stel je eigen plan samen
              </button>
            </>
          ) : (
            <>
              {/* Price summary */}
              <div className="mb-3">
                {customModules.size > 0 ? (
                  <>
                    {selectedReKey && !isMixed ? (
                      <div className="flex items-baseline gap-1">
                        <span className="text-xl font-bold text-blue-600" style={{ fontFamily: "'DM Mono', monospace" }}>
                          &euro;{isM2View
                            ? (customPlanPrice.perM2 * (categoryMultipliers[selectedReKey] ?? 1)).toFixed(3)
                            : (customPlanPrice.perUnit * (categoryMultipliers[selectedReKey] ?? 1)).toFixed(2)}
                        </span>
                        <span className="text-[10px] text-blue-600 font-medium">
                          {isM2View ? "/m\u00B2" : "/unit"} /mnd
                        </span>
                      </div>
                    ) : (
                      <div className="space-y-0.5">
                        <div className="flex items-baseline gap-1">
                          <span className="text-lg font-bold text-blue-600" style={{ fontFamily: "'DM Mono', monospace" }}>
                            &euro;{customPlanPrice.perUnit.toFixed(2)}
                          </span>
                          <span className="text-[10px] text-blue-600 font-medium">/unit /mnd</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-lg font-bold text-blue-600" style={{ fontFamily: "'DM Mono', monospace" }}>
                            &euro;{customPlanPrice.perM2.toFixed(3)}
                          </span>
                          <span className="text-[10px] text-blue-600 font-medium">/m&sup2; /mnd</span>
                        </div>
                      </div>
                    )}
                    <p className="text-[9px] text-gray-500 mt-1">{customModules.size} module(s) geselecteerd</p>
                  </>
                ) : (
                  <p className="text-[10px] text-gray-400 italic">Selecteer modules hieronder</p>
                )}
              </div>

              {/* Module picker */}
              <div className="space-y-1 flex-1">
                {allEnabledModules.map((modKey) => {
                  const mod = modulePricing[modKey];
                  const isSelected = customModules.has(modKey);
                  return (
                    <button
                      key={modKey}
                      onClick={() => toggleCustomModule(modKey)}
                      className={`flex items-center gap-2 text-[11px] w-full text-left rounded-md px-2 py-1.5 transition-colors ${
                        isSelected ? "bg-blue-50 border border-blue-200" : "hover:bg-gray-50 border border-transparent"
                      }`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                        isSelected ? "bg-blue-600 border-blue-600" : "border-gray-300"
                      }`}>
                        {isSelected && (
                          <svg viewBox="0 0 10 8" className="w-[8px] h-[8px]">
                            <polyline points="1 4 3.5 7 9 1" fill="none" stroke="white" strokeWidth="2" />
                          </svg>
                        )}
                      </div>
                      <span className={`flex-1 flex items-center gap-1 ${isSelected ? "text-blue-700 font-medium" : "text-gray-600"}`}>
                        <RenderIcon value={mod.icon} size={14} /> {mod.label}
                      </span>
                      {selectedReKey && !isMixed ? (
                        <span className="text-[9px] text-gray-400 tabular-nums">
                          +&euro;{isM2View
                            ? (mod.pricePerM2 * (categoryMultipliers[selectedReKey] ?? 1)).toFixed(3)
                            : (mod.pricePerUnit * (categoryMultipliers[selectedReKey] ?? 1)).toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-[8px] text-gray-400 tabular-nums text-right">
                          &euro;{mod.pricePerUnit.toFixed(2)}/u
                          {" "}&euro;{mod.pricePerM2.toFixed(3)}/m&sup2;
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setShowCustomBuilder(false)}
                className="mt-3 text-[10px] text-gray-400 hover:text-gray-600"
              >
                Terug naar overzicht
              </button>
            </>
          )}
        </div>
      </div>

      {/* Interactive pricing calculator */}
      <div id="pricing-calculator">
        <PricingCalculator
          tierPricing={tierPricing}
          modulePricing={modulePricing}
          rePricingModel={rePricingModel}
          categoryMultipliers={categoryMultipliers}
        />

        {/* Module picker for mixed-use custom builder */}
        {isMixed && showCustomBuilder && (
          <div className="bg-white rounded-xl border border-blue-200 p-5 mx-6 mt-4 mb-6">
            <h3 className="font-bold text-sm mb-1">Selecteer uw modules</h3>
            <p className="text-[10px] text-gray-500 mb-3">
              Kies de modules die u nodig heeft. De prijs wordt berekend op basis van uw portefeuille hierboven.
            </p>

            {customModules.size > 0 && (
              <div className="flex items-baseline gap-3 mb-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                <div>
                  <span className="text-lg font-bold text-blue-600" style={{ fontFamily: "'DM Mono', monospace" }}>
                    &euro;{customPlanPrice.perUnit.toFixed(2)}
                  </span>
                  <span className="text-[10px] text-blue-600 ml-1">/unit /mnd</span>
                </div>
                <div>
                  <span className="text-lg font-bold text-blue-600" style={{ fontFamily: "'DM Mono', monospace" }}>
                    &euro;{customPlanPrice.perM2.toFixed(3)}
                  </span>
                  <span className="text-[10px] text-blue-600 ml-1">/m&sup2; /mnd</span>
                </div>
                <span className="text-[9px] text-gray-500">{customModules.size} module(s)</span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-1.5">
              {allEnabledModules.map((modKey) => {
                const mod = modulePricing[modKey];
                const isSelected = customModules.has(modKey);
                return (
                  <button
                    key={modKey}
                    onClick={() => toggleCustomModule(modKey)}
                    className={`flex items-center gap-2 text-[11px] w-full text-left rounded-md px-2.5 py-2 transition-colors ${
                      isSelected ? "bg-blue-50 border border-blue-200" : "hover:bg-gray-50 border border-gray-100"
                    }`}
                  >
                    <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                      isSelected ? "bg-blue-600 border-blue-600" : "border-gray-300"
                    }`}>
                      {isSelected && (
                        <svg viewBox="0 0 10 8" className="w-[8px] h-[8px]">
                          <polyline points="1 4 3.5 7 9 1" fill="none" stroke="white" strokeWidth="2" />
                        </svg>
                      )}
                    </div>
                    <span className={`flex-1 flex items-center gap-1 ${isSelected ? "text-blue-700 font-medium" : "text-gray-600"}`}>
                      <RenderIcon value={mod.icon} size={14} /> {mod.label}
                    </span>
                    <span className="text-[8px] text-gray-400 tabular-nums text-right">
                      &euro;{mod.pricePerUnit.toFixed(2)}/u &euro;{mod.pricePerM2.toFixed(3)}/m&sup2;
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Functionality matrix */}
      <div className="px-6 py-10">
        <FunctionalityMatrix modulePricing={modulePricing} tierPricing={tierPricing} />
      </div>

      {/* Footer */}
      <div className="text-center py-5 text-[11px] text-gray-500 border-t">
        Alle prijzen zijn exclusief btw. Jaarlijkse facturatie geeft 2 maanden gratis.
        Boven {customPricingThresholdUnits} units of {customPricingThresholdM2.toLocaleString("nl-BE")} m&sup2; &rarr; pricing op maat.
      </div>
    </div>
  );
};
